import { Box3, Sphere, Vector3 } from "three";
import { computeFitDistance } from "./CameraUtils";

export type CameraPresetId =
  | "front"
  | "back"
  | "top"
  | "left"
  | "right"
  | "leftCenter"
  | "leftEdge"
  | "backLeftEdge"
  | "rightEdge"
  | "rightCenter"
  | "bottomLeft"
  | "perspective";

export const CAMERA_PRESET_IDS: CameraPresetId[] = [
  "front",
  "back",
  "top",
  "left",
  "right",
  "leftCenter",
  "leftEdge",
  "backLeftEdge",
  "rightEdge",
  "rightCenter",
  "bottomLeft",
  "perspective",
];

/**
 * Eski kayıt: `position = center + p * fitDistance`, `target = center + t * fitDistance`.
 * String: `-0.8,0.4,0.7|0,0,0`
 */
export type CameraViewExpression = {
  position: [number, number, number];
  target: [number, number, number];
};

export const CAMERA_PRESET_EXPRESSIONS: Record<
  CameraPresetId,
  CameraViewExpression
> = {
  front: { position: [0, 0, 1], target: [0, 0, 0] },
  back: { position: [0, 0, -1], target: [0, 0, 0] },
  perspective: { position: [-0.8, 0.4, 0.7], target: [0, 0, 0] },
  top: { position: [0, 1, 0], target: [0, 0, 0] },
  left: { position: [-1, 0, 0], target: [0, 0, 0] },
  right: { position: [1, 0, 0], target: [0, 0, 0] },
  leftCenter: { position: [-0.3, 0.2, 0.5], target: [-0.15, 0, 0] },
  leftEdge: { position: [-0.5, 0, 0.35], target: [0, 0, 0] },
  backLeftEdge: { position: [-0.4, 0, 0.4], target: [0, 0, -0.1] },
  rightEdge: { position: [0, 0, 1], target: [0, 0, 0] },
  rightCenter: { position: [0, 0, 1], target: [0, 0, 0] },
  bottomLeft: { position: [0, 0, 1], target: [0, 0, 0] },
};

export interface CameraPresetResult {
  position: Vector3;
  target: Vector3;
}

/** Yeni kayıt: origin (`o:`) veya kutu merkezi (`b:`). `legacy` eski fit-distance ifadesi. */
export type CameraAnchorMode = "origin" | "box" | "legacy";

/** Köşe: bl=sol arka, br=sağ arka, fl=sol ön, fr=sağ ön. */
export type BoxCorner = "bl" | "br" | "fl" | "fr";

type AxisTerm = { x: number; y: number; z: number; r: number; m: number };
type AxisTriple = [AxisTerm, AxisTerm, AxisTerm];
type AnchoredExpr = {
  mode: "origin" | "box";
  corner?: BoxCorner;
  position: AxisTriple;
  target: AxisTriple;
};

const _sphere = new Sphere();
const _center = new Vector3();
const _pivot = new Vector3();
const _half = new Vector3();

/** `o:` sıfır noktası: bbox’un bulunduğu yerin merkezi (world 0 değil). */
function copyBoxAnchorOrigin(box: Box3, out: Vector3): Vector3 {
  return box.getCenter(out);
}

function copyBoxFootprintOrigin(box: Box3, out: Vector3): Vector3 {
  return copyBoxAnchorOrigin(box, out);
}

function boxSphereRadius(box: Box3): number {
  box.getBoundingSphere(_sphere);
  return Math.max(_sphere.radius, 1e-6);
}

const _fmt = (n: number) => {
  const s = n.toFixed(4).replace(/\.?0+$/, "");
  return s === "-0" ? "0" : s;
};

const isNamedPreset = (preset: string): preset is CameraPresetId =>
  preset in CAMERA_PRESET_EXPRESSIONS;

const looksAnchored = (raw: string) =>
  raw.startsWith("o:") || raw.startsWith("b:");

function parseLegacyExpression(raw: string): CameraViewExpression | null {
  const text = raw.trim();
  if (looksAnchored(text) || !text.includes("|")) return null;
  const [posPart, targetPart] = text.split("|");
  const p = posPart.split(",").map(Number);
  const t = targetPart.split(",").map(Number);
  if (
    p.length !== 3 ||
    t.length !== 3 ||
    p.some((n) => !Number.isFinite(n)) ||
    t.some((n) => !Number.isFinite(n))
  ) {
    return null;
  }
  return { position: [p[0], p[1], p[2]], target: [t[0], t[1], t[2]] };
}

function formatLegacyExpression(expr: CameraViewExpression): string {
  const p = expr.position;
  const t = expr.target;
  return `${_fmt(p[0])},${_fmt(p[1])},${_fmt(p[2])}|${_fmt(t[0])},${_fmt(t[1])},${_fmt(t[2])}`;
}

function evaluateLegacy(
  expr: CameraViewExpression,
  center: Vector3,
  distance: number,
): CameraPresetResult {
  return {
    position: new Vector3(
      center.x + expr.position[0] * distance,
      center.y + expr.position[1] * distance,
      center.z + expr.position[2] * distance,
    ),
    target: new Vector3(
      center.x + expr.target[0] * distance,
      center.y + expr.target[1] * distance,
      center.z + expr.target[2] * distance,
    ),
  };
}

function legacyFromPose(
  position: Vector3,
  target: Vector3,
  center: Vector3,
  distance: number,
): CameraViewExpression {
  const d = distance === 0 ? 1 : distance;
  return {
    position: [
      (position.x - center.x) / d,
      (position.y - center.y) / d,
      (position.z - center.z) / d,
    ],
    target: [
      (target.x - center.x) / d,
      (target.y - center.y) / d,
      (target.z - center.z) / d,
    ],
  };
}

const emptyTerm = (): AxisTerm => ({ x: 0, y: 0, z: 0, r: 0, m: 0 });

const axisHalf = (min: number, max: number) => Math.max((max - min) * 0.5, 1e-6);

const copyBoxHalfExtent = (box: Box3, out: Vector3): Vector3 => {
  out.set(
    axisHalf(box.min.x, box.max.x),
    axisHalf(box.min.y, box.max.y),
    axisHalf(box.min.z, box.max.z),
  );
  return out;
};

/** Slot değeri = sıfır + Σ (harf × o harfin büyüklüğü). `x/y/z` = halfExtent, `r` = sphere.r */
const evalSlot = (
  zero: number,
  half: Vector3,
  radius: number,
  term: AxisTerm,
) =>
  zero +
  term.x * half.x +
  term.y * half.y +
  term.z * half.z +
  term.r * radius +
  term.m;

const encodeOriginAxis = (
  value: number,
  origin: number,
  radius: number,
): AxisTerm => {
  const delta = value - origin;
  if (Math.abs(delta) < 1e-6) return emptyTerm();
  return { x: 0, y: 0, z: 0, r: delta / radius, m: 0 };
};

/** Kutu kaydı: seçilen köşenin X/Z yüzü (−1/+1) + metre; Y merkezden halfExtent. */
const encodeBoxFace = (
  value: number,
  face: number,
  size: "x" | "z",
  sign: -1 | 1,
): AxisTerm => {
  const term = emptyTerm();
  term[size] = sign;
  term.m = value - face;
  if (Math.abs(term.m) < 1e-6) term.m = 0;
  return term;
};

const encodeBoxAxis = (
  value: number,
  center: number,
  half: number,
  size: "x" | "y" | "z",
): AxisTerm => {
  const delta = value - center;
  if (Math.abs(delta) < 1e-6) return emptyTerm();
  const term = emptyTerm();
  term[size] = delta / Math.max(half, 1e-6);
  return term;
};

const appendSigned = (out: string, value: number, suffix: string) => {
  if (Math.abs(value) < 1e-6) return out;
  const chunk = `${_fmt(value)}${suffix}`;
  if (!out) return chunk;
  return value > 0 ? `${out}+${chunk}` : `${out}${chunk}`;
};

const formatAxis = (term: AxisTerm) => {
  let out = "";
  out = appendSigned(out, term.x, "x");
  out = appendSigned(out, term.y, "y");
  out = appendSigned(out, term.z, "z");
  out = appendSigned(out, term.r, "r");
  if (Math.abs(term.m) >= 1e-6) {
    out = out
      ? term.m > 0
        ? `${out}+${_fmt(term.m)}`
        : `${out}${_fmt(term.m)}`
      : _fmt(term.m);
  }
  return out || "0";
};

const formatTriple = (terms: AxisTriple) =>
  `${formatAxis(terms[0])},${formatAxis(terms[1])},${formatAxis(terms[2])}`;

const parseAxis = (raw: string): AxisTerm | null => {
  const text = raw.trim();
  if (text === "0") return emptyTerm();
  const term = emptyTerm();
  const re = /([+-]?(?:\d*\.\d+|\d+))([xyzr])?/g;
  let consumed = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    if (match.index !== consumed) return null;
    consumed = match.index + match[0].length;
    const val = Number(match[1]);
    if (!Number.isFinite(val)) return null;
    const unit = match[2];
    if (!unit) term.m += val;
    else term[unit as "x" | "y" | "z" | "r"] += val;
  }
  if (consumed === 0 || consumed !== text.length) return null;
  return term;
};

const parseTriple = (raw: string): AxisTriple | null => {
  const parts = raw.split(",");
  if (parts.length !== 3) return null;
  const x = parseAxis(parts[0]);
  const y = parseAxis(parts[1]);
  const z = parseAxis(parts[2]);
  if (!x || !y || !z) return null;
  return [x, y, z];
};

const LEGACY_CORNER: Record<string, BoxCorner> = {
  lb: "bl",
  rb: "br",
  lf: "fl",
  rf: "fr",
};

function parseBoxCorner(raw: string): BoxCorner | undefined {
  if (raw === "bl" || raw === "br" || raw === "fl" || raw === "fr") return raw;
  return LEGACY_CORNER[raw];
}

function parseAnchored(raw: string): AnchoredExpr | null {
  const text = raw.trim();
  if (!looksAnchored(text)) return null;
  const mode = text.startsWith("o:") ? "origin" : "box";
  let body = text.slice(2);
  let corner: BoxCorner | undefined;
  const cornerMatch = body.match(/^(bl|br|fl|fr|lb|lf|rb|rf):/);
  if (cornerMatch) {
    corner = parseBoxCorner(cornerMatch[1]);
    body = body.slice(cornerMatch[0].length);
  }
  if (!body.includes("|")) return null;
  const [posPart, targetPart] = body.split("|");
  const position = parseTriple(posPart);
  const target = parseTriple(targetPart);
  if (!position || !target) return null;
  return { mode, corner, position, target };
}

function formatAnchored(expr: AnchoredExpr): string {
  if (expr.mode === "origin") {
    return `o:${formatTriple(expr.position)}|${formatTriple(expr.target)}`;
  }
  const corner = expr.corner ?? "bl";
  return `b:${corner}:${formatTriple(expr.position)}|${formatTriple(expr.target)}`;
}

function anchoredFromPose(
  position: Vector3,
  target: Vector3,
  box: Box3,
  origin: Vector3,
  mode: "origin" | "box",
  corner?: BoxCorner,
): AnchoredExpr {
  const half = copyBoxHalfExtent(box, _half);
  const radius = boxSphereRadius(box);
  const center = copyBoxAnchorOrigin(box, _center);
  const encodeOrigin = (v: number, axis: "x" | "y" | "z") =>
    encodeOriginAxis(v, center[axis], radius);
  const encodeBox = (v: number, axis: "x" | "y" | "z") => {
    if (!corner || axis === "y") {
      return encodeBoxAxis(v, center[axis], half[axis], axis);
    }
    const isNeg =
      axis === "x" ? corner.endsWith("l") : corner.startsWith("b");
    const sign: -1 | 1 = isNeg ? -1 : 1;
    const face = isNeg ? box.min[axis] : box.max[axis];
    return encodeBoxFace(v, face, axis, sign);
  };
  const encode = mode === "origin" ? encodeOrigin : encodeBox;
  return {
    mode,
    corner: mode === "box" ? corner : undefined,
    position: [
      encode(position.x, "x"),
      encode(position.y, "y"),
      encode(position.z, "z"),
    ],
    target: [encode(target.x, "x"), encode(target.y, "y"), encode(target.z, "z")],
  };
}

function evaluateAnchored(
  expr: AnchoredExpr,
  box: Box3,
  _origin: Vector3,
): CameraPresetResult {
  const radius = boxSphereRadius(box);
  const half = copyBoxHalfExtent(box, _half);
  const zero = copyBoxAnchorOrigin(box, _pivot);
  const point = (terms: AxisTriple) => {
    const slot = (term: AxisTerm, a: "x" | "y" | "z") =>
      evalSlot(zero[a], half, radius, term);
    return new Vector3(slot(terms[0], "x"), slot(terms[1], "y"), slot(terms[2], "z"));
  };
  return { position: point(expr.position), target: point(expr.target) };
}

export function encodeCameraPresetFromPose(
  position: Vector3,
  target: Vector3,
  box: Box3,
  origin: Vector3,
  fitDistance: number,
  mode: CameraAnchorMode = "legacy",
  corner?: BoxCorner,
): string {
  if (mode === "origin" || mode === "box") {
    return formatAnchored(
      anchoredFromPose(position, target, box, origin, mode, corner),
    );
  }
  box.getCenter(_center);
  return formatLegacyExpression(
    legacyFromPose(position, target, _center, fitDistance),
  );
}

/**
 * Named (`perspective`), eski `-0.8,0.4,0.7|0,0,0`, yeni `o:…|…` / `b:…|…`.
 * Bozuk `o:`/`b:` string front’a düşmez (eski kaydı ezmemek için).
 */
export function getCameraPresetFromBox(
  box: Box3,
  preset: CameraPresetId | string,
  fovDeg: number,
  aspect: number,
  origin?: Vector3,
): CameraPresetResult | null {
  const text = preset.trim();
  box.getCenter(_center);
  box.getBoundingSphere(_sphere);
  const distance = computeFitDistance(
    Math.max(_sphere.radius, 1e-3),
    fovDeg,
    aspect,
    "legacy-aspect",
  );

  if (looksAnchored(text)) {
    const anchored = parseAnchored(text);
    if (!anchored) return null;
    return evaluateAnchored(anchored, box, origin ?? _pivot.copy(_center));
  }

  const legacy = parseLegacyExpression(text);
  if (legacy) return evaluateLegacy(legacy, _center, distance);
  if (isNamedPreset(text)) {
    return evaluateLegacy(CAMERA_PRESET_EXPRESSIONS[text], _center, distance);
  }
  return evaluateLegacy(CAMERA_PRESET_EXPRESSIONS.front, _center, distance);
}
